import React from "react";

interface MarkdownFormatterProps {
  content: string;
  baseTextClass?: string;
  h1Class?: string;
  h2Class?: string;
  h3Class?: string;
  listItemClass?: string;
  boldClass?: string;
}

// Función para procesar texto con formato negrita **texto**
const processInlineFormatting = (
  text: string,
  baseClass: string,
  boldClass: string
) => {
  const parts: (string | React.JSX.Element)[] = [];
  let currentIndex = 0;
  let key = 0;

  // Regex para encontrar texto en negrita **texto**
  const boldRegex = /\*\*(.*?)\*\*/g;
  let match;

  while ((match = boldRegex.exec(text)) !== null) {
    // Agregar texto antes del match
    if (match.index > currentIndex) {
      parts.push(text.slice(currentIndex, match.index));
    }

    // Agregar texto en negrita
    parts.push(
      <span key={key++} className={boldClass}>
        {match[1]}
      </span>
    );

    currentIndex = match.index + match[0].length;
  }

  // Agregar texto restante
  if (currentIndex < text.length) {
    parts.push(text.slice(currentIndex));
  }

  return parts.length > 0 ? parts : [text];
};

export const formatMarkdownContent = ({
  content,
  baseTextClass = "text-sm font-lufga text-salbox-neutral-600 mb-2 leading-5",
  h1Class = "text-xl font-lufga-semibold mb-4 text-salbox-neutral-900",
  h2Class = "text-lg font-lufga-semibold mb-2 text-salbox-neutral-900",
  h3Class = "text-base font-lufga-semibold mb-2 text-salbox-neutral-800",
  listItemClass = "text-sm font-lufga mb-2 text-salbox-neutral-600 leading-5 ml-4",
  boldClass = "font-lufga-semibold text-salbox-neutral-900",
}: MarkdownFormatterProps) => {
  if (!content) return null;

  const lines = content.split("\n");
  const formattedContent: React.JSX.Element[] = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Skip empty lines - no extra spacing
    if (!line) {
      continue;
    }

    // Handle H1 headers (# text)
    if (line.startsWith("# ") && !line.startsWith("## ")) {
      const headerText = line.replace("# ", "").trim();
      const formattedText = processInlineFormatting(
        headerText,
        h1Class,
        boldClass
      );
      formattedContent.push(
        <h1 key={key++} className={h1Class}>
          {formattedText}
        </h1>
      );
      continue;
    }

    // Handle H2 headers (## text)
    if (line.startsWith("## ") && !line.startsWith("### ")) {
      const headerText = line.replace("## ", "").trim();
      const formattedText = processInlineFormatting(
        headerText,
        h2Class,
        boldClass
      );
      formattedContent.push(
        <h2 key={key++} className={h2Class}>
          {formattedText}
        </h2>
      );
      continue;
    }

    // Handle H3 headers (### text)
    if (line.startsWith("### ")) {
      const headerText = line.replace("### ", "").trim();
      const formattedText = processInlineFormatting(
        headerText,
        h3Class,
        boldClass
      );
      formattedContent.push(
        <h3 key={key++} className={h3Class}>
          {formattedText}
        </h3>
      );
      continue;
    }

    // Handle list items (- text)
    if (line.startsWith("- ")) {
      const listText = line.replace("- ", "").trim();
      const formattedText = processInlineFormatting(
        listText,
        listItemClass,
        boldClass
      );
      formattedContent.push(
        <p key={key++} className={listItemClass}>
          • {formattedText}
        </p>
      );
      continue;
    }

    // Handle regular paragraphs
    if (line) {
      const formattedText = processInlineFormatting(
        line,
        baseTextClass,
        boldClass
      );
      formattedContent.push(
        <p key={key++} className={baseTextClass}>
          {formattedText}
        </p>
      );
    }
  }

  return <>{formattedContent}</>;
};

// Hook para usar en componentes
export const useMarkdownFormatter = () => {
  return { formatMarkdownContent };
};